import {getFirstSelectedEmailMarketingCampaign, getSelectedItemIdAtIndex, INPUT_SELECTED_ITEM_ID_INDEX} from '@Libraries/email-marketing-campaigns-library';
import {MY_STUFF_HASHES} from '@Libraries/my-stuff-library';
import {PMW_COLORS_100} from '@Utils/color.util';
import {onAjaxError} from '@Utils/ajax.util';
import type {AppDispatch} from '@/store';
import {isRejected} from '@reduxjs/toolkit';
import type {UnknownAction} from '@reduxjs/toolkit';
import {
  fetchCampaignRecipientInsights,
  fetchCampaignRecipientInsightsTotalRows,
  fetchMailingListRecipientInsights,
  fetchMailingListRecipientInsightsTotalRows,
  fetchNegativeOutcomeCounts,
} from './email-marketing-performance-thunk';
import type {
  EmailMarketingCampaign,
  EmailMarketingCampaignPerformanceState,
  FetchCampaignRecipientInsightsArg,
  FetchCampaignRecipientInsightsTotalRowsArg,
  FetchMailingListRecipientInsightsArg,
  FetchMailingListRecipientInsightsTotalRowsArg,
  FetchNegativeOutcomeCountsArg,
  Metrics,
  NegativeOutcomeRecipientCounts,
  PerformanceInputs,
  RecipientInsightsPaging,
  SingleCampaignMetrics,
  UserMailingList,
} from './email-marketing-campaign-performance.types';
import {
  COMPARISON_VIEW_SCREEN_WIDTH_BREAKPOINT_PX,
  FIRST_PAGE_NUMBER,
  MetricItemType,
  RECIPIENT_INSIGHTS_DEFAULT_PAGE_LIMIT,
  RecipientInsightsSortDirection,
  RecipientInsightsSortField,
} from './email-marketing-campaign-performance.types';

const PUBLISH_ON_MONTH_OFFSET = 1;

export const PERFORMANCE_ERROR_CODES = {
  PREMIUM_UNDERLEVELED: 'premium-underleveled',
} as const;

export const METRIC_TYPES = {
  DELIVERY: 'delivery',
  CLICK: 'click',
  OPEN: 'open',
} as const;

export function getMetricDetailsForType(metrics: SingleCampaignMetrics, metricType: string): Metrics {
  switch (metricType) {
    case METRIC_TYPES.DELIVERY:
      return metrics.delivery;
    case METRIC_TYPES.OPEN:
      return metrics.open;
    case METRIC_TYPES.CLICK:
      return metrics.click;
    default:
      throw new Error(`Unknown metric type: ${metricType}`);
  }
}

export function getUnsubscribedRecipientCount(negativeOutcomeCounts?: NegativeOutcomeRecipientCounts): number | null {
  return negativeOutcomeCounts?.numUnsubscribedRecipients ?? null;
}

export function getInvalidRecipientCount(negativeOutcomeCounts?: NegativeOutcomeRecipientCounts): number | null {
  return negativeOutcomeCounts?.numInvalidRecipients ?? null;
}

export const INPUT_HASH_INDEX = {
  ITEM_TYPE: 2,
  FIRST_SELECTED_ITEM_ID: 3,
  SECOND_SELECTED_ITEM_ID: 4,
} as const;

export const DEFAULT_CAMPAIGN_METRIC_GRAPH_COLORS = {
  Primary: PMW_COLORS_100.PRIMARY,
  Secondary: PMW_COLORS_100.SECONDARY,
  Tertiary: PMW_COLORS_100.SUCCESS,
} as const;

const RECIPIENT_INSIGHTS_SORT_FIELD_VALUES: string[] = Object.values(RecipientInsightsSortField);
const RECIPIENT_INSIGHTS_SORT_DIRECTION_VALUES: string[] = Object.values(RecipientInsightsSortDirection);
const METRIC_ITEM_TYPE_VALUES: string[] = Object.values(MetricItemType);

export function isMetricItemType(value: string): value is MetricItemType {
  return METRIC_ITEM_TYPE_VALUES.includes(value);
}

export function isRecipientInsightsSortField(value: unknown): value is RecipientInsightsSortField {
  return typeof value === 'string' && RECIPIENT_INSIGHTS_SORT_FIELD_VALUES.includes(value);
}

function isRecipientInsightsSortDirection(value: string): value is RecipientInsightsSortDirection {
  return RECIPIENT_INSIGHTS_SORT_DIRECTION_VALUES.includes(value);
}

export function getRecipientInsightsSortDirectionFromTableSortOrder(tableSortOrder: string): RecipientInsightsSortDirection {
  return isRecipientInsightsSortDirection(tableSortOrder) ? tableSortOrder : RecipientInsightsSortDirection.ASC;
}

function narrowSelectedItemId(rawSelectedItemId: unknown): string | null {
  return typeof rawSelectedItemId === 'string' ? rawSelectedItemId : null;
}

export function getFirstSelectedItemId(selectedItemIds: string[]): string | null {
  return narrowSelectedItemId(getSelectedItemIdAtIndex(selectedItemIds, INPUT_SELECTED_ITEM_ID_INDEX.FIRST_ID));
}

export function getSecondSelectedItemId(selectedItemIds: string[]): string | null {
  return narrowSelectedItemId(getSelectedItemIdAtIndex(selectedItemIds, INPUT_SELECTED_ITEM_ID_INDEX.SECOND_ID));
}

function getEmailMarketingCampaignsPerformanceStateFromReduxStore(): EmailMarketingCampaignPerformanceState {
  return window.PMW.redux.store.getState().emailMarketingCampaignsPerformance;
}

export function getRequestedRecipientInsightsPaging(): RecipientInsightsPaging {
  return getEmailMarketingCampaignsPerformanceStateFromReduxStore().detailsRequestedPaging;
}

export async function fetchRecipientInsightsPage(dispatch: AppDispatch, request: FetchCampaignRecipientInsightsArg): Promise<void> {
  notifyRecipientInsightsFetchFailed(await dispatch(fetchCampaignRecipientInsights(request)));
}

export async function fetchMailingListRecipientInsightsPage(dispatch: AppDispatch, request: FetchMailingListRecipientInsightsArg): Promise<void> {
  notifyRecipientInsightsFetchFailed(await dispatch(fetchMailingListRecipientInsights(request)));
}

export async function fetchNegativeOutcomeCountsForItem(dispatch: AppDispatch, request: FetchNegativeOutcomeCountsArg): Promise<void> {
  notifyRecipientInsightsFetchFailed(await dispatch(fetchNegativeOutcomeCounts(request)));
}

export async function fetchMailingListRecipientInsightsTotalRowCount(dispatch: AppDispatch, request: FetchMailingListRecipientInsightsTotalRowsArg): Promise<void> {
  notifyRecipientInsightsFetchFailed(await dispatch(fetchMailingListRecipientInsightsTotalRows(request)));
}

export async function fetchCampaignRecipientInsightsTotalRowCount(dispatch: AppDispatch, request: FetchCampaignRecipientInsightsTotalRowsArg): Promise<void> {
  notifyRecipientInsightsFetchFailed(await dispatch(fetchCampaignRecipientInsightsTotalRows(request)));
}

function notifyRecipientInsightsFetchFailed(resultAction: UnknownAction): void {
  if (isRejected(resultAction)) {
    onAjaxError(resultAction.error, window.i18next.t('pmwjs_error_message'), false);
  }
}

export function getRecipientInsightsOffsetForPage(page: number, limit: number): number {
  return (page - FIRST_PAGE_NUMBER) * limit;
}

export function getDefaultRecipientInsightsPaging(): RecipientInsightsPaging {
  return {
    offset: 0,
    limit: RECIPIENT_INSIGHTS_DEFAULT_PAGE_LIMIT,
    sortField: RecipientInsightsSortField.EMAIL,
    sortDir: RecipientInsightsSortDirection.ASC,
  };
}

export function getEmailMarketingCampaignSelectedItemTypeFromStore(): MetricItemType {
  return getEmailMarketingCampaignsPerformanceStateFromReduxStore().itemType;
}

export function getFirstSelectedMailingList(): UserMailingList | null {
  const {selectedItemIds, mailingLists} = getEmailMarketingCampaignsPerformanceStateFromReduxStore();
  const firstSelectedItemId = getFirstSelectedItemId(selectedItemIds);

  if (firstSelectedItemId === null) {
    return null;
  }

  return mailingLists[firstSelectedItemId] ?? null;
}

export function isEmailMarketingCampaign(item: unknown): item is EmailMarketingCampaign {
  return item !== null && typeof item === 'object' && 'publishOn' in item;
}

export function isUserMailingList(item: unknown): item is UserMailingList {
  return item !== null && typeof item === 'object' && 'isSmart' in item;
}

export function formatCampaignPublishOnDate(publishOn: string): string {
  const [datePart] = publishOn.split(' ');
  const [month, day, year] = datePart.split('-').map(Number);
  const publishDate = new Date(year, month - PUBLISH_ON_MONTH_OFFSET, day);
  if (Number.isNaN(publishDate.getTime())) {
    return publishOn;
  }

  return publishDate.toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'});
}

export function getFirstSelectedItem(): EmailMarketingCampaign | UserMailingList | null {
  if (getEmailMarketingCampaignSelectedItemTypeFromStore() === MetricItemType.CAMPAIGN) {
    const firstSelectedCampaign: unknown = getFirstSelectedEmailMarketingCampaign();
    return isEmailMarketingCampaign(firstSelectedCampaign) ? firstSelectedCampaign : null;
  }

  return getFirstSelectedMailingList();
}

export function updateWindowLocationHash(itemType: MetricItemType, selectedItemIds: string[], shouldReplaceHistoryEntry = false): void {
  let computedHash = `${MY_STUFF_HASHES.emailCampaignPerformance}/${itemType}`;

  if (selectedItemIds.length > 0) {
    computedHash += `/${selectedItemIds[INPUT_SELECTED_ITEM_ID_INDEX.FIRST_ID]}`;
  }
  if (selectedItemIds.length > 1) {
    computedHash += `/${selectedItemIds[INPUT_SELECTED_ITEM_ID_INDEX.SECOND_ID]}`;
  }

  if (shouldReplaceHistoryEntry) {
    window.history.replaceState(window.history.state, '', computedHash);
    return;
  }

  window.location.hash = computedHash;
}

const isMobileScreenWidth = (): boolean => {
  return window.innerWidth <= COMPARISON_VIEW_SCREEN_WIDTH_BREAKPOINT_PX;
};

export function getPerformanceInputsFromURL(): PerformanceInputs {
  const segments = window.location.hash.split('/');
  const itemType = segments[INPUT_HASH_INDEX.ITEM_TYPE] ?? MetricItemType.CAMPAIGN;

  const requestedItemIds = segments.slice(INPUT_HASH_INDEX.FIRST_SELECTED_ITEM_ID, INPUT_HASH_INDEX.SECOND_SELECTED_ITEM_ID + 1);
  const uniqueItemIds = [...new Set(requestedItemIds)];

  if (isMobileScreenWidth()) {
    return {itemType, selectedItemIds: uniqueItemIds.slice(0, 1)};
  }

  return {itemType, selectedItemIds: uniqueItemIds};
}
